import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Typography, message } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts';
import {
  GetProfileDetailsCardUserDocument,
  useAddUserFavoriteInProfileSidebarMutation,
  useGetProfileDetailsCardLocationAndScheduleQuery,
  useGetProfileDetailsCardUserQuery,
  useRemoveUserFavoriteInProfileSidebarMutation,
} from 'generated/graphql';
import {
  ProfileDetailsCard,
  ProfileDetailsCardSkeleton,
  ProfileDetailsCardUser,
} from './ProfileDetailsCard';
import { getScheduledDaysFromWeeklySchedule } from './formatWorkingHours';

type ProfileDetailsCardContainerProps = {
  userId: string;
};

export const ProfileDetailsCardContainer = ({
  userId,
}: ProfileDetailsCardContainerProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar');
  const { currentUser } = useAuthContext();

  const { data, loading, error } = useGetProfileDetailsCardUserQuery({
    variables: { userId },
    skip: !userId,
    errorPolicy: 'all',
  });

  const accountUser =
    data?.getAccountUsersByIds?.find((u) => u?.id === userId) ?? null;
  const userById = data?.getUserById ?? null;
  const defaultLocationId = userById?.defaultLocation ?? null;

  // Location and schedule are supplementary: if the query fails the card
  // still renders with the data it has, rather than failing entirely.
  const { data: locationAndScheduleData, loading: locationAndScheduleLoading } =
    useGetProfileDetailsCardLocationAndScheduleQuery({
      variables: {
        locationId: defaultLocationId ?? '',
        locationIdStr: defaultLocationId ?? '',
        userId,
      },
      skip: !defaultLocationId || !userId,
      errorPolicy: 'all',
    });

  const scheduledDays = useMemo(
    () =>
      getScheduledDaysFromWeeklySchedule(
        locationAndScheduleData?.employeeVisit?.getWeeklyScheduleForUser
      ),
    [locationAndScheduleData]
  );

  const user: ProfileDetailsCardUser | null = accountUser
    ? {
        ...accountUser,
        timeZone: userById?.timeZone ?? null,
        workingHours: userById?.workingHours ?? null,
        defaultLocation: locationAndScheduleData?.getLocationById ?? null,
      }
    : null;

  const isFavorite = user?.isMyFavorite ?? false;

  const refetchQueries = [
    { query: GetProfileDetailsCardUserDocument, variables: { userId } },
  ];
  const [addUserToFavorites, { loading: adding }] =
    useAddUserFavoriteInProfileSidebarMutation({ refetchQueries });
  const [removeUserFromFavorites, { loading: removing }] =
    useRemoveUserFavoriteInProfileSidebarMutation({ refetchQueries });

  const toggleFavorite = useCallback(async () => {
    try {
      if (isFavorite) {
        await removeUserFromFavorites({ variables: { userId } });
      } else {
        await addUserToFavorites({ variables: { userId } });
      }
    } catch {
      message.error(t('favoriteError'));
    }
  }, [addUserToFavorites, removeUserFromFavorites, isFavorite, userId, t]);

  if ((loading && !user) || locationAndScheduleLoading) {
    return <ProfileDetailsCardSkeleton />;
  }

  if (error || !user) {
    return (
      <ErrorWrapper>
        <Typography.Text type="secondary">{t('errorLoading')}</Typography.Text>
      </ErrorWrapper>
    );
  }

  return (
    <ProfileDetailsCard
      user={user}
      scheduledDays={scheduledDays}
      isFavorite={isFavorite}
      onToggleFavorite={toggleFavorite}
      favoriteToggleDisabled={adding || removing}
      showFavoriteToggle={currentUser?.id !== user.id}
    />
  );
};

const ErrorWrapper = styled.div`
  padding: 16px;
`;
